import React, { useEffect, useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { backendUrl } from '../config';

// Utilise le worker PDF local depuis le dossier public (compatible CRA)
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export default function PdfApp({ initialUuid7, initialPath, filePath }) {
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [error, setError] = useState('');
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [currentFile, setCurrentFile] = useState(null);
    const [explorerOpen, setExplorerOpen] = useState(false);
    const [explorerPath, setExplorerPath] = useState('/');
    const [explorerFiles, setExplorerFiles] = useState([]);
    const [explorerLoading, setExplorerLoading] = useState(false);
    const [explorerError, setExplorerError] = useState('');

    // Charge le PDF depuis le backend sécurisé
    useEffect(() => {
        const fetchPdf = async () => {
            if (!initialUuid7 || !initialPath) return;
            setError('');
            setPdfBlobUrl(null);
            try {
                const token = window.localStorage.getItem('token');
                const res = await fetch(`${backendUrl}/pdf?uuid7=${encodeURIComponent(initialUuid7)}&path=${encodeURIComponent(initialPath)}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error('Erreur lors du chargement du PDF');
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                setPdfBlobUrl(url);
            } catch (e) {
                setError(e.message || 'Erreur lors du chargement du PDF');
            }
        };
        fetchPdf();
        return () => {
            if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
        };
        // eslint-disable-next-line
    }, [initialUuid7, initialPath]);

    // Fonction utilitaire pour charger un PDF depuis une URL
    const loadPdfFromUrl = useCallback(async (url, authToken) => {
        try {
            const response = await fetch(url, {
                headers: authToken ? { 
                    'Authorization': `Bearer ${authToken}`,
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                } : {}
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.detail || 
                    `Erreur HTTP ${response.status} lors du chargement du PDF`
                );
            }

            const blob = await response.blob();
            if (!blob.type.includes('application/pdf')) {
                throw new Error('Le fichier sélectionné n\'est pas un PDF valide');
            }

            const blobUrl = URL.createObjectURL(blob);
            setPdfBlobUrl(blobUrl);
            setError('');
            return blobUrl;
        } catch (error) {
            console.error('Erreur lors du chargement du PDF:', error);
            setError(error.message || 'Erreur lors du chargement du PDF');
            setPdfBlobUrl(null);
            throw error;
        }
    }, []);

    // Nettoie les URLs d'objets blob lors du démontage du composant
    useEffect(() => {
        return () => {
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
            }
        };
    }, [pdfBlobUrl]);

    // Charge un PDF depuis l'explorateur de fichiers
    const handleSelectExplorerFile = useCallback((uuid7, relPath) => {
        setError('');
        setPdfBlobUrl(null);
        
        const token = window.localStorage.getItem('token');
        if (!token) {
            setError('Authentification requise');
            return;
        }

        // Construit l'URL de manière sécurisée
        const pdfUrl = new URL(
            `/pdf?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`,
            backendUrl
        ).toString();

        loadPdfFromUrl(pdfUrl, token);
    }, [loadPdfFromUrl]);

    // Ouvre l'explorateur pour choisir un PDF
    const handleOpenFromExplorer = async () => {
        setExplorerOpen(true);
        setExplorerPath('/');
        fetchExplorerFiles('/');
    };

    // Charge la liste des fichiers/dossiers depuis le backend
    const fetchExplorerFiles = async (path) => {
        setExplorerLoading(true);
        setExplorerError('');
        try {
            const userStr = window.localStorage.getItem('user');
            let uuid7 = null;
            if (userStr) {
                try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
            }
            if (!uuid7) throw new Error('Utilisateur non trouvé');
            const token = window.localStorage.getItem('token');
            let relPath = path === '/' ? '' : path.replace(/^\//, '');
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const apiFiles = apiUrl ? `${apiUrl}/files` : '/files';
            const res = await fetch(apiFiles + `?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Erreur lors du chargement des fichiers');
            const data = await res.json();
            setExplorerFiles(data.filter(f => f.name.endsWith('.pdf')));
        } catch (e) {
            setExplorerError("Erreur lors du chargement des fichiers.");
            setExplorerFiles([]);
        } finally {
            setExplorerLoading(false);
        }
    };

    // Sélection d'un PDF dans l'explorateur
    const handleExplorerSelectFile = (file) => {
        const userStr = window.localStorage.getItem('user');
        let uuid7 = null;
        if (userStr) {
            try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
        }
        let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
        handleSelectExplorerFile(uuid7, relPath);
        setExplorerOpen(false);
    };

    // --- useEffect pour ouverture directe d'un fichier depuis filePath (explorer) ---
    useEffect(() => {
        async function openFileFromProps() {
            if (typeof filePath === 'string' && filePath.length > 0) {
                try {
                    setExplorerLoading(true);
                    setExplorerError('');
                    let uuid7 = null;
                    try {
                        const userStr = window.localStorage.getItem('user');
                        if (userStr) {
                            const user = JSON.parse(userStr);
                            uuid7 = user.uuid7 || user.uuid || user.id || null;
                        }
                    } catch (e) {}
                    if (!uuid7) throw new Error("Impossible de trouver l'identifiant utilisateur (uuid7)");
                    const relPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
                    handleSelectExplorerFile(uuid7, relPath);
                } catch (e) {
                    setExplorerError(e.message || "Erreur lors de l'ouverture du fichier depuis l'explorateur");
                } finally {
                    setExplorerLoading(false);
                }
            }
        }
        if (typeof filePath !== 'undefined' && filePath) {
            openFileFromProps();
        }
        // eslint-disable-next-line
    }, [filePath]);

    // --- useEffect pour ouverture directe d'un fichier depuis localStorage (fallback) ---
    useEffect(() => {
        let effectiveFilePath = filePath;
        if (!effectiveFilePath) {
            // Fallback : lire depuis localStorage si présent
            const stored = window.localStorage.getItem('notepadApp_filePath');
            if (stored) {
                effectiveFilePath = stored;
                window.localStorage.removeItem('notepadApp_filePath');
            }
        }
        async function openFileFromStorage() {
            if (typeof effectiveFilePath === 'string' && effectiveFilePath.length > 0) {
                try {
                    setExplorerLoading(true);
                    setExplorerError('');
                    let uuid7 = null;
                    try {
                        const userStr = window.localStorage.getItem('user');
                        if (userStr) {
                            const user = JSON.parse(userStr);
                            uuid7 = user.uuid7 || user.uuid || user.id || null;
                        }
                    } catch (e) {}
                    if (!uuid7) throw new Error("Impossible de trouver l'identifiant utilisateur (uuid7)");
                    const relPath = effectiveFilePath.startsWith('/') ? effectiveFilePath.slice(1) : effectiveFilePath;
                    handleSelectExplorerFile(uuid7, relPath);
                } catch (e) {
                    setExplorerError(e.message || "Erreur lors de l'ouverture du fichier depuis l'explorateur");
                } finally {
                    setExplorerLoading(false);
                }
            }
        }
        if (typeof effectiveFilePath !== 'undefined' && effectiveFilePath) {
            openFileFromStorage();
        }
        // eslint-disable-next-line
    }, [filePath]);

    // --- useEffect pour ouverture directe d'un fichier depuis localStorage (pdfApp_filePath) ---
    useEffect(() => {
        const path = window.localStorage.getItem('pdfApp_filePath');
        if (path) {
            const userStr = window.localStorage.getItem('user');
            let uuid7 = null;
            if (userStr) {
                try {
                    const user = JSON.parse(userStr);
                    uuid7 = user.uuid7 || user.uuid || user.id || null;
                } catch {}
            }
            if (uuid7) {
                const relPath = path.startsWith('/') ? path.slice(1) : path;
                handleSelectExplorerFile(uuid7, relPath);
            }
        }
        // eslint-disable-next-line
    }, []);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setPageNumber(1);
    }

    return (
        <div style={{ padding: 24, background: '#f4f4f4', minHeight: 600 }}>
            <h3>Lecteur PDF (react-pdf, sécurisé)</h3>
            <button onClick={handleOpenFromExplorer}>Ouvrir PDF</button>
            {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
            <div style={{ border: '1px solid #ccc', minHeight: 600, maxHeight: '80vh', overflow: 'auto' }}>
                {pdfBlobUrl ? (
                    <Document
                        file={pdfBlobUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading="Chargement du PDF..."
                        error="Erreur lors du chargement du PDF"
                    >
                        <Page pageNumber={pageNumber} />
                    </Document>
                ) : (
                    <span style={{ color: '#888' }}>Aucun PDF sélectionné</span>
                )}
                {numPages && (
                    <div style={{ marginTop: 12, textAlign: 'center' }}>
                        <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1}>Précédent</button>
                        <span style={{ margin: '0 12px' }}>Page {pageNumber} / {numPages}</span>
                        <button onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages}>Suivant</button>
                    </div>
                )}
            </div>
            {/* Explorer minimaliste */}
            {explorerOpen && (
                <div style={{ position: 'fixed', left: 0, top: 0, width: '50vw', height: '50vh', background: 'rgba(0,0,0,0.25)', zIndex: 20, display: 'flex', alignItems: '', justifyContent: 'left' }}>
                    <div style={{ background: '#fff', borderRadius: 8, padding: 24, minWidth: 600, minHeight: 600, boxShadow: '0 4px 24px #0002', maxHeight: '80vh', overflowY: 'auto' }}>
                        <h3>Ouvrir un PDF</h3>
                        <div style={{ marginBottom: 8 }}>Dossier : <b>{explorerPath}</b></div>
                        {explorerLoading ? <span>Chargement...</span> : (
                            <ul style={{ maxHeight: 200, overflowY: 'auto', padding: 0 }}>
                                {explorerFiles.map((file, idx) => (
                                    <li key={idx} style={{ margin: 0, padding: 6, cursor: 'pointer', color: '#1976d2' }} onClick={() => handleExplorerSelectFile(file)}>
                                        {file.name}
                                    </li>
                                ))}
                                {explorerFiles.length === 0 && <li style={{ color: '#888' }}>Aucun PDF ici</li>}
                            </ul>
                        )}
                        {explorerError && <div style={{ color: 'red', marginTop: 8 }}>{explorerError}</div>}
                        <div style={{ marginTop: 16 }}>
                            <button onClick={() => setExplorerOpen(false)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}